import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Phone,
  MessageSquare,
  Lock,
  ArrowUp,
  Clock,
  DollarSign,
  ShieldAlert,
  Calendar,
  ChevronDown,
  ChevronRight,
  Smartphone,
  Shield,
  CheckCircle2,
  XCircle,
  Wifi,
  WifiOff,
  Info,
  Settings,
  RefreshCw,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ButtonSpinner } from '@/components/feedback';
import { EmptyState } from '@/components/feedback/EmptyState';
import { useActiveTenantId } from '@/features/tenants/use-active-tenant';
import { cn, formatCurrency } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import { useToast } from '@/hooks/use-toast';
import { hasPermission } from '@/lib/auth/permissions';
import api, { handleApiResponse, type ApiError } from '@/lib/api';
import { OperationResult } from '@/lib/operation-result';
import type { BackendOverdueContract } from '@/features/dashboard/dashboard';
import { sendSMS } from '@/features/notifications/api';
import { lockDeviceUnit } from '@/features/inventory/api';
import type { CollectionPriority } from '@/types';
import { useState } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { ListFilterBar } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';

/* ── Escalation Pipeline Stages ───────────────────────── */

const escalationStages = [
  {
    stage: '1st Failure',
    trigger: 'ZAAD deduction fails once',
    systemAction: 'Send reminder, retry later as per schedule.',
    deviceAction: 'Keep active',
    message:
      'We could not process your installment. Please top up your ZAAD wallet.',
    color: 'bg-amber-500',
    textColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
  },
  {
    stage: '2nd Failure',
    trigger: 'Second failed deduction',
    systemAction: 'Send second reminder and mark customer as at-risk.',
    deviceAction: 'Keep active / warning',
    message:
      'Your installment is still pending. Please top up to avoid restriction.',
    color: 'bg-orange-500',
    textColor: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-500/10',
  },
  {
    stage: '3rd Failure',
    trigger: 'Third failed deduction or grace nearing expiry',
    systemAction: 'Send final warning and create collections task.',
    deviceAction: 'Warning / soft restriction',
    message: 'Final reminder: Please pay to avoid device restriction.',
    color: 'bg-red-400',
    textColor: 'text-red-500 dark:text-red-400',
    bgColor: 'bg-red-400/10',
  },
  {
    stage: '4th+ Failure',
    trigger: 'Repeated failures after grace period',
    systemAction: 'Apply policy-based restriction and collections escalation.',
    deviceAction: 'Restrict / Lock',
    message: 'Your device access has been restricted due to overdue payment.',
    color: 'bg-red-600',
    textColor: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-600/10',
  },
  {
    stage: 'Payment Received',
    trigger: 'Any valid payment after warning/restriction',
    systemAction: 'Update balance and restore if threshold met.',
    deviceAction: 'Unlock / Restore',
    message: 'Payment received. Your device has been restored.',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
];

/* ── Exception Cases ──────────────────────────────────── */

const exceptionCases = [
  {
    exception: 'Wallet inactive / blocked',
    detection: 'ZAAD API response',
    action: 'Notify customer, pause deduction retry.',
    owner: 'Customer Support / ZAAD Ops',
    resolution: 'Customer resolves wallet issue; retry deduction.',
    icon: <WifiOff className="w-4 h-4" />,
  },
  {
    exception: 'Insufficient funds',
    detection: 'ZAAD API response',
    action: 'Retry deduction and send top-up reminder.',
    owner: 'System',
    resolution: 'Payment collected once wallet is funded.',
    icon: <DollarSign className="w-4 h-4" />,
  },
  {
    exception: 'Partial funds available',
    detection: 'ZAAD API response / policy',
    action: 'Collect partial if allowed; update remaining balance.',
    owner: 'System',
    resolution: 'Continue retry for remaining amount.',
    icon: <DollarSign className="w-4 h-4" />,
  },
  {
    exception: 'Duplicate payment',
    detection: 'Duplicate reference or same amount/time',
    action: 'Flag for reconciliation and prevent double posting.',
    owner: 'Finance / Branch Manager',
    resolution: 'Apply as credit or refund with approval.',
    icon: <AlertTriangle className="w-4 h-4" />,
  },
  {
    exception: 'Customer dispute',
    detection: 'Support complaint/ticket',
    action: 'Freeze corrective action and open investigation.',
    owner: 'Branch Manager / Tenant Admin',
    resolution: 'Resolve complaint, adjust balance if justified.',
    icon: <Info className="w-4 h-4" />,
  },
  {
    exception: 'Agent delayed sync',
    detection: 'Offline queue delay threshold exceeded',
    action: 'Alert Branch Manager and mark agent for review.',
    owner: 'Branch Manager',
    resolution: 'Sync payment or investigate cash handling.',
    icon: <Wifi className="w-4 h-4" />,
  },
  {
    exception: 'Cash collected but not posted',
    detection: 'Customer complaint or reconciliation variance',
    action: 'Open fraud/reconciliation case.',
    owner: 'Branch Manager / Finance',
    resolution: 'Post valid payment or escalate fraud action.',
    icon: <XCircle className="w-4 h-4" />,
  },
  {
    exception: 'ZAAD API downtime',
    detection: 'System monitoring/API error',
    action: 'Queue deductions and notify operations.',
    owner: 'Tech Ops',
    resolution: 'Retry automatically when service recovers.',
    icon: <WifiOff className="w-4 h-4" />,
  },
  {
    exception: 'Device command failure',
    detection: 'Device lock/unlock API failure',
    action: 'Queue command and alert device operations.',
    owner: 'Tech Ops',
    resolution: 'Retry command; manual exception if required.',
    icon: <Smartphone className="w-4 h-4" />,
  },
  {
    exception: 'Manual adjustment required',
    detection: 'Wrong amount, wrong customer, dispute',
    action: 'Lock record for supervisor review.',
    owner: 'Branch Manager / Tenant Admin',
    resolution: 'Approved correction with audit trail.',
    icon: <Settings className="w-4 h-4" />,
  },
];

const PRIORITY_VALUES = ['critical', 'high', 'medium', 'low'] as const;

const collectionsSearchSchema = z.object({
  q: z.string().optional(),
  branchId: z.string().optional(),
  status: z.enum(PRIORITY_VALUES).optional(),
});

export const Route = createFileRoute('/_layout/collections/')({
  validateSearch: collectionsSearchSchema,
  component: CollectionsPage,
});

/** One overdue *contract*, aggregated from its overdue installments. */
interface CollectionItem {
  contractId: string;
  customerName: string;
  customerPhone: string;
  deviceInfo: string;
  /** Null when the contract has no unit attached — Lock Device is hidden. */
  deviceId: string | null;
  deviceLocked: boolean;
  /** Sum across every overdue installment on the contract. */
  outstandingAmount: number;
  /** How many installments were collapsed into this card. */
  installmentsOverdue: number;
  /** Age of the *oldest* miss — what the priority is driven by. */
  daysOverdue: number;
  priority: CollectionPriority;
}

function computePriority(daysOverdue: number): CollectionPriority {
  if (daysOverdue >= 60) return 'critical';
  if (daysOverdue >= 30) return 'high';
  if (daysOverdue >= 7) return 'medium';
  return 'low';
}

/**
 * Collapse the overdue feed into one card per contract.
 *
 * `/v1/dashboard/overdue` emits ONE ROW PER OVERDUE INSTALLMENT, so a contract
 * that has missed three payments arrives three times — previously rendering as
 * three near-identical cards (and three React children keyed by the same empty
 * string). Collections works the account, not the individual instalment, so we
 * sum what is owed and keep the worst age.
 */
function aggregateOverdue(rows: BackendOverdueContract[]): CollectionItem[] {
  const byContract = new Map<string, CollectionItem>();

  for (const row of rows) {
    const contractId = row.contractId ?? '';
    // Without a contract id there is nothing to act on — drop rather than
    // render an inert card.
    if (!contractId) continue;

    const amount = Number(row.overdueAmount ?? 0);
    const days = Number(row.daysOverdue ?? 0);
    const existing = byContract.get(contractId);

    if (existing) {
      existing.outstandingAmount += amount;
      existing.installmentsOverdue += 1;
      existing.daysOverdue = Math.max(existing.daysOverdue, days);
      existing.priority = computePriority(existing.daysOverdue);
      continue;
    }

    byContract.set(contractId, {
      contractId,
      customerName: row.customerName || 'Unknown customer',
      customerPhone: row.customerPhone || '',
      deviceInfo: row.deviceInfo || 'Device',
      deviceId: row.deviceId ?? null,
      deviceLocked: Boolean(row.deviceLocked),
      outstandingAmount: amount,
      installmentsOverdue: 1,
      daysOverdue: days,
      priority: computePriority(days),
    });
  }

  // Worst-first: the oldest debt is the one an officer should work next.
  return [...byContract.values()].sort((a, b) => b.daysOverdue - a.daysOverdue);
}

/** Resolving every contract's schedule against loan-service is slow on a cold
 *  cache; the client-wide 30s would abort it and read as an empty book. */
const COLLECTIONS_TIMEOUT_MS = 90_000;

async function fetchCollections(branchId?: string): Promise<CollectionItem[]> {
  const result = await handleApiResponse<BackendOverdueContract[]>(() =>
    api.get<OperationResult<BackendOverdueContract[]>>(
      '/v1/dashboard/overdue',
      {
        params: branchId ? { branchId } : undefined,
        timeout: COLLECTIONS_TIMEOUT_MS,
      }
    )
  );
  return aggregateOverdue(result.value ?? []);
}

const priorityCfg: Record<
  CollectionPriority,
  { label: string; cls: string; dot: string }
> = {
  low: {
    label: 'Low',
    cls: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
    dot: 'bg-blue-500',
  },
  medium: {
    label: 'Medium',
    cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
    dot: 'bg-amber-500',
  },
  high: {
    label: 'High',
    cls: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30',
    dot: 'bg-orange-500',
  },
  critical: {
    label: 'Critical',
    cls: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
    dot: 'bg-red-500',
  },
};

/* ── Per-account actions ──────────────────────────────────
 *
 * Lives in its own component so each card owns its mutations and dialog
 * state — a page-level mutation would spin every row's button at once.
 */
function CollectionActions({
  item,
  currency,
}: Readonly<{ item: CollectionItem; currency: string }>) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const [reminderOpen, setReminderOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [notifyOnLock, setNotifyOnLock] = useState(true);
  // Every card renders this dialog, so the checkbox id must be row-unique.
  const notifyCheckboxId = `lock-notify-${item.contractId}`;

  // Backend gates locking on `device_units:lock`; mirror it here so an
  // operator without the scope never sees a button the API will reject.
  const canLock = hasPermission(user, 'device_units', 'lock');
  const amountLabel = formatCurrency(item.outstandingAmount, currency);
  // Matches the code the automated escalation SMS quotes, so a customer
  // reading both messages sees one consistent reference.
  const paymentCode = item.contractId.slice(0, 8).toUpperCase();

  // `lock_warning` copy is reserved for accounts old enough that restriction
  // is genuinely next; younger ones get the plain overdue nudge.
  const reminderTemplate =
    item.priority === 'critical' || item.priority === 'high'
      ? 'lock_warning'
      : 'payment_overdue';

  const smsParams = {
    name: item.customerName,
    amount: amountLabel,
    days_overdue: String(item.daysOverdue),
    payment_code: paymentCode,
  };

  const reminderMutation = useMutation({
    mutationFn: () =>
      sendSMS({
        recipientPhone: item.customerPhone,
        template: reminderTemplate,
        params: smsParams,
      }),
    // sendSMS swallows transport errors into `succeeded: false` rather than
    // throwing, so both outcomes land here.
    onSuccess: (result) => {
      setReminderOpen(false);
      if (result.succeeded) {
        toast({
          title: 'Reminder sent',
          description: `SMS delivered to ${item.customerPhone}.`,
        });
      } else {
        toast({
          title: 'Reminder failed',
          description: result.errors?.[0] ?? 'The SMS could not be sent.',
          variant: 'destructive',
        });
      }
    },
  });

  const lockMutation = useMutation({
    mutationFn: async () => {
      const result = await lockDeviceUnit(
        item.deviceId as string,
        `Collections: ${item.daysOverdue}d overdue, ${amountLabel} outstanding`
      );
      // The lock endpoint does not notify the customer, so a silent lock
      // leaves them with a dead handset and no explanation. Best-effort —
      // a failed SMS must not read as a failed lock.
      if (notifyOnLock && item.customerPhone) {
        await sendSMS({
          recipientPhone: item.customerPhone,
          template: 'device_locked',
          params: {
            name: item.customerName,
            amount: amountLabel,
            payment_code: paymentCode,
          },
        });
      }
      return result;
    },
    onSuccess: () => {
      setLockOpen(false);
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast({
        title: 'Device locked',
        description: notifyOnLock
          ? `${item.deviceInfo} locked and the customer was notified.`
          : `${item.deviceInfo} locked.`,
      });
    },
    // handleApiResponse throws ApiError on `succeeded: false`, so failures
    // surface here rather than in onSuccess.
    onError: (error) => {
      const apiErr = error as ApiError;
      toast({
        title: 'Lock failed',
        description:
          apiErr?.errors?.[0] ??
          apiErr?.message ??
          'The device was not locked.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {/* ── Send Reminder ── outward-facing, so it confirms first ── */}
      <AlertDialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            disabled={!item.customerPhone}
            title={
              item.customerPhone
                ? undefined
                : 'No phone number on file for this customer'
            }
          >
            <MessageSquare className="h-3 w-3" />
            Send Reminder
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send payment reminder</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  An SMS goes to{' '}
                  <span className="font-medium text-foreground">
                    {item.customerName}
                  </span>{' '}
                  at{' '}
                  <span className="font-mono text-foreground">
                    {item.customerPhone}
                  </span>
                  , in their own language.
                </p>
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between gap-4">
                    <span>Message</span>
                    <span className="font-medium text-foreground">
                      {reminderTemplate === 'lock_warning'
                        ? 'Final warning before device lock'
                        : 'Payment overdue'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>Amount quoted</span>
                    <span className="font-medium text-foreground">
                      {amountLabel}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>Days overdue</span>
                    <span className="font-medium text-foreground">
                      {item.daysOverdue}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>Payment code</span>
                    <span className="font-mono font-medium text-foreground">
                      {paymentCode}
                    </span>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reminderMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                reminderMutation.mutate();
              }}
              disabled={reminderMutation.isPending}
            >
              {reminderMutation.isPending ? (
                <>
                  <ButtonSpinner className="mr-2" />
                  Sending...
                </>
              ) : (
                'Send reminder'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Call ── hands off to the OS dialer; a real anchor so it works
          on desktop softphones and mobile alike ── */}
      <Button
        asChild={!!item.customerPhone}
        variant="outline"
        size="sm"
        className="h-8 text-xs gap-1.5"
        disabled={!item.customerPhone}
      >
        {item.customerPhone ? (
          <a href={`tel:${item.customerPhone.replace(/[^\d+]/g, '')}`}>
            <Phone className="h-3 w-3" />
            Call
          </a>
        ) : (
          <span>
            <Phone className="h-3 w-3" />
            Call
          </span>
        )}
      </Button>

      {/* ── Lock Device ── destructive + irreversible from the customer's
          side, so it confirms and states the consequence plainly ── */}
      {!item.deviceLocked && item.deviceId && canLock && (
        <AlertDialog open={lockOpen} onOpenChange={setLockOpen}>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 text-red-500 hover:text-red-600"
            >
              <Lock className="h-3 w-3" />
              Lock Device
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">
                Lock this device?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    <span className="font-medium text-foreground">
                      {item.deviceInfo}
                    </span>{' '}
                    will be restricted on{' '}
                    <span className="font-medium text-foreground">
                      {item.customerName}
                    </span>
                    &rsquo;s handset. They lose access until the account is
                    brought current or an operator unlocks it.
                  </p>
                  <p className="text-xs">
                    {item.daysOverdue} days overdue &middot; {amountLabel}{' '}
                    outstanding
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex items-start gap-2.5 rounded-lg border bg-muted/30 p-3">
              <Checkbox
                id={notifyCheckboxId}
                checked={notifyOnLock}
                onCheckedChange={(v) => setNotifyOnLock(v === true)}
                disabled={!item.customerPhone}
                className="mt-0.5"
              />
              {/* htmlFor rather than a wrapping <label>: Radix renders the
                  checkbox as a <button>, which a wrapping label does not
                  activate. */}
              <label
                htmlFor={notifyCheckboxId}
                className="text-xs cursor-pointer"
              >
                <span className="font-medium">Tell the customer why</span>
                <span className="block text-muted-foreground mt-0.5">
                  {item.customerPhone
                    ? `Sends a device-locked SMS to ${item.customerPhone} with the payment code.`
                    : 'No phone number on file for this customer.'}
                </span>
              </label>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={lockMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => {
                  e.preventDefault();
                  lockMutation.mutate();
                }}
                disabled={lockMutation.isPending}
              >
                {lockMutation.isPending ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    Locking...
                  </>
                ) : (
                  'Lock device'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* ── Escalate ── opens the contract, where the escalation ladder,
          lock countdown and payment history all live ── */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs gap-1.5 text-orange-500 hover:text-orange-600"
        onClick={() =>
          navigate({
            to: '/contracts/$contractId',
            params: { contractId: item.contractId },
          })
        }
      >
        <ArrowUp className="h-3 w-3" />
        Escalate
      </Button>
    </div>
  );
}

function CollectionsPage() {
  const { t } = useTranslation();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  const search = Route.useSearch();
  const [showPipeline, setShowPipeline] = useState(true);
  const [showExceptions, setShowExceptions] = useState(false);

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search, to: '/collections' });

  const tenantId = useActiveTenantId();
  const {
    data: collections = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['collections', tenantId, search.branchId],
    queryFn: () => fetchCollections(search.branchId),
    placeholderData: (previous) => previous,
    retry: 1,
  });
  // A failed fetch has no numbers to show; zeros would read as "nothing owed".
  const hasNoData = isError && collections.length === 0;
  const errorMessage =
    error instanceof Error && error.message
      ? error.message
      : 'The overdue book could not be loaded.';

  // Priority is derived from `daysOverdue` after the fact, so it can only be
  // filtered here; the endpoint returns the whole overdue book unpaginated,
  // which makes client-side narrowing complete rather than partial.
  const priorityFilter = search.status ?? 'all';
  const priorityChips: ReadonlyArray<
    FilterChip<'all' | (typeof PRIORITY_VALUES)[number]>
  > = [
    { value: 'all', label: 'All' },
    ...PRIORITY_VALUES.map((value) => ({
      value,
      label: priorityCfg[value].label,
      icon: (
        <span
          className={cn('h-2 w-2 rounded-full', priorityCfg[value].dot)}
          aria-hidden
        />
      ),
    })),
  ];
  const term = (debouncedSearch ?? '').toLowerCase();
  const filtered = collections.filter((c) => {
    const matchesSearch =
      !term ||
      c.customerName.toLowerCase().includes(term) ||
      c.customerPhone.includes(term);
    const matchesPriority =
      priorityFilter === 'all' || c.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  const totalOutstanding = filtered.reduce(
    (s, c) => s + c.outstandingAmount,
    0
  );
  const criticalCases = filtered.filter(
    (c) => c.priority === 'critical'
  ).length;
  const devicesLocked = filtered.filter((c) => c.deviceLocked).length;
  const avgDaysOverdue =
    filtered.length > 0
      ? Math.round(
          filtered.reduce((s, c) => s + c.daysOverdue, 0) / filtered.length
        )
      : 0;

  const unavailable = '—';
  const stats = [
    {
      label: 'Total Outstanding',
      value: hasNoData
        ? unavailable
        : formatCurrency(totalOutstanding, currency),
      icon: DollarSign,
      color: 'from-red-500 to-red-600',
    },
    {
      label: 'Critical Cases',
      value: hasNoData ? unavailable : criticalCases,
      icon: AlertTriangle,
      color: 'from-orange-500 to-orange-600',
    },
    {
      label: 'Devices Locked',
      value: hasNoData ? unavailable : devicesLocked,
      icon: ShieldAlert,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: 'Avg Days Overdue',
      value: hasNoData ? unavailable : `${avgDaysOverdue}d`,
      icon: Calendar,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Collections</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Overdue accounts & enforcement actions
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Collections</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Overdue accounts & enforcement actions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasNoData ? (
            <Badge className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm">
              <XCircle className="h-4 w-4 mr-1.5" />
              Unavailable
            </Badge>
          ) : collections.length === 0 ? (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 px-3 py-1.5 text-sm">
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              All accounts current
            </Badge>
          ) : (
            <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30 px-3 py-1.5 text-sm">
              <AlertTriangle className="h-4 w-4 mr-1.5" />
              {collections.length} overdue{' '}
              {collections.length === 1 ? 'account' : 'accounts'}
            </Badge>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refresh collections"
            title="Refresh"
          >
            <RefreshCw
              className={cn('h-4 w-4', isFetching && 'animate-spin')}
            />
          </Button>
        </div>
      </div>

      {isError && collections.length > 0 && (
        <div
          role="alert"
          className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="flex-1">
            Couldn&apos;t refresh — showing the last loaded figures.{' '}
            <span className="text-muted-foreground">{errorMessage}</span>
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar
        chips={priorityChips}
        chipValue={priorityFilter}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        chipsAriaLabel="Filter by priority"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.collections')}
        isSearchPending={isSearchPending}
        branchId={search.branchId}
        onBranchChange={(branchId) => setFilters({ branchId })}
        activeCount={activeCount}
        onReset={reset}
        isPending={isFetching}
      />

      {/* Collection Cards */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Overdue Accounts{hasNoData ? '' : ` (${filtered.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {hasNoData ? (
            <EmptyState
              variant="error"
              title="Couldn't load overdue accounts"
              description={`${errorMessage} The figures above are hidden until the book loads, so they can't be mistaken for a clean book.`}
              action={{
                label: isFetching ? 'Retrying…' : 'Try again',
                onClick: () => void refetch(),
              }}
              className="py-14"
            />
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                {activeCount > 0
                  ? 'No accounts match your filters'
                  : 'Nothing overdue'}
              </p>
              <p className="text-sm">
                {activeCount > 0
                  ? 'Try adjusting your search or priority filter.'
                  : 'Every active contract is up to date on its installments.'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((c) => {
                const cfg = priorityCfg[c.priority];
                const initials = c.customerName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <div
                    key={c.contractId}
                    className={cn(
                      'flex hover:bg-muted/30 transition-colors',
                      c.priority === 'critical' && 'bg-red-500/[0.02]'
                    )}
                  >
                    <div className={cn('w-1.5 shrink-0 rounded-l', cfg.dot)} />
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center text-sm font-semibold text-red-500 shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold">
                              <Link
                                to="/contracts/$contractId"
                                params={{ contractId: c.contractId }}
                                className="rounded-sm transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                              >
                                {c.customerName}
                              </Link>
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {c.customerPhone || 'No phone on file'}
                            </p>
                          </div>
                        </div>
                        <Badge className={cn('shrink-0', cfg.cls)}>
                          <span
                            className={cn(
                              'h-1.5 w-1.5 rounded-full mr-1.5',
                              cfg.dot
                            )}
                          />
                          {cfg.label}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="font-medium text-foreground">
                            {c.daysOverdue}d
                          </span>{' '}
                          overdue
                        </div>
                        <div>
                          <span className="font-medium text-red-500">
                            {formatCurrency(c.outstandingAmount, currency)}
                          </span>{' '}
                          outstanding
                          {/* Two contracts for the same customer look
                              identical without this — and it explains why the
                              figure is larger than a single instalment. */}
                          {c.installmentsOverdue > 1 && (
                            <span className="text-xs">
                              {' '}
                              across {c.installmentsOverdue} instalments
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Smartphone className="h-3.5 w-3.5" />
                          {c.deviceInfo}
                        </div>
                        {c.deviceLocked && (
                          <div className="flex items-center gap-1.5 text-red-500">
                            <Lock className="h-3.5 w-3.5" />
                            Device locked
                          </div>
                        )}
                      </div>
                      <CollectionActions item={c} currency={currency} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Escalation Pipeline */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowUp className="w-5 h-5 text-primary" />
                Retry & Escalation Pipeline
              </CardTitle>
              <CardDescription className="text-xs">
                Automated escalation stages for failed deductions
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPipeline(!showPipeline)}
              className="gap-1"
            >
              {showPipeline ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showPipeline ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {showPipeline && (
          <CardContent>
            <div className="flex flex-col lg:flex-row gap-3">
              {escalationStages.map((stage, idx) => (
                <div key={stage.stage} className="flex-1 flex items-stretch">
                  <Card
                    className={cn(
                      'flex-1 border-0 shadow-sm overflow-hidden relative',
                      stage.bgColor
                    )}
                  >
                    <div
                      className={cn(
                        'absolute top-0 left-0 w-1 h-full',
                        stage.color
                      )}
                    />
                    <CardContent className="p-4 pl-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge
                          className={cn(
                            'text-[10px] font-bold',
                            stage.color,
                            'text-white'
                          )}
                        >
                          {idx + 1}
                        </Badge>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            stage.textColor
                          )}
                        >
                          {stage.stage}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-2">
                        <span className="font-medium">Trigger:</span>{' '}
                        {stage.trigger}
                      </p>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        <span className="font-medium">System:</span>{' '}
                        {stage.systemAction}
                      </p>
                      <p className="text-[11px] text-muted-foreground mb-2">
                        <span className="font-medium">Device:</span>{' '}
                        {stage.deviceAction}
                      </p>
                      <div className="mt-auto pt-2 border-t border-black/5">
                        <p className="text-[10px] italic text-muted-foreground">
                          "{stage.message}"
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  {idx < escalationStages.length - 1 && (
                    <div className="hidden lg:flex items-center px-1">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Exception Handling */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" />
                Exception Handling
              </CardTitle>
              <CardDescription className="text-xs">
                Known edge cases and their resolution ownership
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExceptions(!showExceptions)}
              className="gap-1"
            >
              {showExceptions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showExceptions ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {showExceptions && (
          <CardContent className="p-0">
            <div className="divide-y divide-border/50">
              {exceptionCases.map((exc) => (
                <div
                  key={exc.exception}
                  className="flex flex-col sm:flex-row gap-3 p-4 hover:bg-muted/20 transition-colors"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5">
                      {exc.icon}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{exc.exception}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <span className="font-medium">Detection:</span>{' '}
                        {exc.detection}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <span className="font-medium">Action:</span>{' '}
                        {exc.action}
                      </p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                        <span className="font-medium text-muted-foreground">
                          Resolution:
                        </span>{' '}
                        {exc.resolution}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] self-start shrink-0"
                  >
                    {exc.owner}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
